import React from 'react'
import { ArrayInput, FormDataConsumer, SimpleFormIterator } from 'react-admin'
import { activityStatusChoices } from 'services/cfg/form'
import { FormDataConsumerProps, WithId } from 'types/common'
import LocalizedAutocomplete from 'ui-shared/LocalizedFields/LocalizedAutocomplete'
import { BundlePackageToggle } from './BundlePackageToggle'

import {
  validateIsBundledPackage,
  validatePresetMinAndMaxCompatibility,
  validateUniqueById,
} from 'services/utils/validation'
import { getById } from 'services/utils'
import LastEditDetailsField from 'ui-shared/FormComponents/LastEditDetailsFields'
import LocalizedInputText from 'ui-shared/LocalizedFields/LocalizedInputText'
import DescriptionInput from 'ui-shared/WrappedInputs/DescriptionInput'
import { WithSectionAndStatus } from 'ui-shared/CustomInputs/CustomReferenceInput'
import { TailoredProduct } from 'services/api/entities/catalog/types'
import { hasIntersection } from 'resources/operations/events/utils'
import { pkgByCustomersAndOrgs } from './eventPackage/package/common'
import KSelectInput from 'ui-shared/WrappedInputs/KSelectInput'

type Props<T> = {
  defaultBundleSetting?: boolean
  isToggleBundleVisible?: boolean
  linkedRulesPanel?: JSX.Element
  choices: T[]
  source?: string
  presetInputFields?: React.ReactElement
  eventPkgCustomers?: number[]
  eventPkgOrgs?: number[]
  showAllPkgs?: boolean
}

export type GenericChoiceTailored = WithId &
  WithSectionAndStatus &
  Omit<TailoredProduct, 'customersFull' | 'organizationsFull'>

const ProductPackageBaseFormContent = <T extends GenericChoiceTailored>({
  presetInputFields,
  defaultBundleSetting,
  isToggleBundleVisible,
  choices,
  source = '',
  eventPkgCustomers = [],
  eventPkgOrgs = [],
  showAllPkgs = false, // if true: show all packages privates and publics.
  ...props
}: Props<T>) => {
  const activeChoices = choices.filter((c) => c.status !== 'inactive')
  const filteredChoices = showAllPkgs
    ? activeChoices
    : pkgByCustomersAndOrgs(activeChoices, eventPkgCustomers, eventPkgOrgs)
  return (
    <>
      <LastEditDetailsField />
      <LocalizedInputText
        source={`${source}name`}
        label="form.fields.name"
        fullWidth
      />
      <DescriptionInput source={`${source}description`} />
      <KSelectInput
        label="form.fields.status"
        source={`${source}status`}
        defaultValue={'active'}
        choices={activityStatusChoices}
      />
      <BundlePackageToggle
        defaultBundleSetting={defaultBundleSetting}
        isVisible={isToggleBundleVisible}
        source={source}
      />
      <ArrayInput
        source={`${source}products`}
        {...props}
        validate={[
          validateUniqueById,
          validatePresetMinAndMaxCompatibility(filteredChoices),
          hasIntersection(filteredChoices),
          validateIsBundledPackage(filteredChoices),
        ]}
      >
        <SimpleFormIterator>
          <LocalizedAutocomplete
            label={'form.fields.name'}
            source="id"
            choices={filteredChoices}
          />

          <FormDataConsumer>
            {({
              scopedFormData,
              getSource,
            }: FormDataConsumerProps<unknown, { id: number }>) => {
              if (scopedFormData && presetInputFields) {
                return React.cloneElement(presetInputFields, {
                  getSource,
                  selectedProduct: getById(filteredChoices, scopedFormData.id),
                })
              }
            }}
          </FormDataConsumer>
        </SimpleFormIterator>
      </ArrayInput>
    </>
  )
}

export default ProductPackageBaseFormContent
